/**
 * Instrument Detail Tabs
 * 
 * Tab container with 6 tabs:
 * 1. Details (overview)
 * 2. Holdings - All holdings (Asset scope only)
 * 3. Valuation - Pricing method + valuation history
 * 4. Return - TBD
 * 5. Notes - Private notes
 * 6. Documents - File uploads
 */

import { useEffect, useState } from 'react';
import { logger } from '@/services/LoggingService';
import { cn } from '@/lib/utils';
import { OverviewTab } from './tabs/OverviewTab';
import { ValuationTab } from './tabs/ValuationTab';
import { HoldingsTab } from './tabs/HoldingsTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { ReturnsTab } from './tabs/ReturnsTab';
import { NotesTab } from './tabs/NotesTab';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';
import type { InstrumentScope } from '@/hooks/useInstrumentDetail';

interface InstrumentDetailTabsProps {
  asset: UnifiedAsset;
  data: any; // InstrumentDetailData from hook
  scope: InstrumentScope;
  onPositionClick: (position: any) => void;
}

type TabValue = 'overview' | 'holdings' | 'valuation' | 'returns' | 'notes' | 'documents';

export function InstrumentDetailTabs({ asset, data, scope, onPositionClick }: InstrumentDetailTabsProps) {
  const showHoldingsTab = scope === 'asset' && asset?.source_type === 'account' && Array.isArray(data?.positions) && data.positions.length > 0;
  try {
    logger.debug('InstrumentDetailTabs', 'Holdings tab decision', {
      scope,
      positionsLength: Array.isArray(data?.positions) ? data.positions.length : undefined,
      sourceType: asset?.source_type,
      showHoldingsTab,
    });
  } catch {}
  const [activeTab, setActiveTab] = useState<TabValue>('overview');
  useEffect(() => {
    if (scope === 'position') setActiveTab('overview');
  }, [scope]);
  const tabs = [
    { id: 'overview' as TabValue, label: 'Details' },
    ...(showHoldingsTab ? [{ id: 'holdings' as TabValue, label: 'Holdings' }] : []),
    { id: 'valuation' as TabValue, label: 'Valuation' },
    { id: 'returns' as TabValue, label: 'Returns' },
    { id: 'notes' as TabValue, label: 'Notes' },
    { id: 'documents' as TabValue, label: 'Documents' },
  ];

  return (
    <div className="mt-8">
      {/* Tab Navigation */}
      <div className="border-b mb-6">
        <div className="flex flex-wrap md:flex-nowrap gap-x-6 gap-y-2 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "pb-2 relative",
                activeTab === tab.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <h3 className="text-base font-medium">{tab.label}</h3>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab
            asset={asset}
            data={data}
            scope={scope}
            onPositionClick={onPositionClick}
          />
        )}

        {activeTab === 'holdings' && showHoldingsTab && (
          <HoldingsTab
            data={data}
            onPositionClick={onPositionClick}
          />
        )}

        {activeTab === 'valuation' && (
          <ValuationTab
            asset={asset}
            data={data}
          />
        )}

        {activeTab === 'returns' && (
          <ReturnsTab />
        )}

        {activeTab === 'notes' && (
          <NotesTab asset={asset} />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab asset={asset} />
        )}
      </div>
    </div>
  );
}
